import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Calendar, Save } from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/hooks/useToast";
import { formatDate } from "@/lib/utils";
import {
  createHoliday,
  deleteHoliday,
  listHolidays,
  listPolicies,
  updateHoliday,
  upsertPolicy,
  type Holiday,
  type LeavePolicy,
  type TenureTier,
} from "@/api/leavePolicy";
import { AccrualRunnerPanel } from "@/components/settings/AccrualRunnerPanel";
import { CustomLeaveTypesPanel } from "@/components/settings/CustomLeaveTypesPanel";

const LEAVE_TYPES = [
  "PTO",
  "SICK",
  "PERSONAL",
  "BEREAVEMENT",
  "FMLA",
  "UNPAID",
  "JURY_DUTY",
  "VOLUNTEER",
  "CAREGIVING",
  "MENTAL_HEALTH",
];

const ACCRUAL_METHODS = [
  { value: "annual", label: "Lump sum (annual)" },
  { value: "monthly", label: "Monthly accrual" },
  { value: "per_pay_period", label: "Per pay period" },
];

interface PolicyForm {
  leave_type: string;
  annual_allocation_days: number;
  accrual_method: string;
  monthly_accrual_days: number;
  max_carryover_days: number;
  requires_approval: boolean;
  min_notice_days: number;
  max_consecutive_days: number;
  is_active: boolean;
  /** When false (default), requests that push the employee below
   *  zero remaining are rejected at submit time. */
  allow_negative_balance: boolean;
  accrual_cap_days: number;
  proration_method: string;
  accrue_day_of_month: number;
  tenure_tiers: TenureTier[];
}

const EMPTY_POLICY: PolicyForm = {
  leave_type: "PTO",
  annual_allocation_days: 15,
  accrual_method: "annual",
  monthly_accrual_days: 0,
  max_carryover_days: 5,
  requires_approval: true,
  min_notice_days: 0,
  max_consecutive_days: 0,
  is_active: true,
  allow_negative_balance: false,
  accrual_cap_days: 0,
  proration_method: "none",
  accrue_day_of_month: 1,
  tenure_tiers: [],
};

const PRORATION_METHODS = [
  { value: "none", label: "No proration" },
  { value: "days", label: "Prorate first month by days worked" },
];

export function LeavePolicySettings() {
  const { addToast } = useToast();
  const [year, setYear] = useState(new Date().getFullYear());
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [policies, setPolicies] = useState<LeavePolicy[]>([]);
  const [holidayOpen, setHolidayOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [hName, setHName] = useState("");
  const [hDate, setHDate] = useState("");
  const [hPaid, setHPaid] = useState(true);

  const [policyOpen, setPolicyOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<LeavePolicy | null>(null);
  const [form, setForm] = useState<PolicyForm>(EMPTY_POLICY);

  const load = async () => {
    const [h, p] = await Promise.all([listHolidays(year), listPolicies()]);
    setHolidays(h);
    setPolicies(p);
  };

  useEffect(() => {
    load().catch(() => addToast("error", "Failed to load policies"));
  }, [year]);

  const saveHoliday = async () => {
    try {
      if (editingHoliday) {
        await updateHoliday(editingHoliday.id, {
          name: hName,
          date: hDate,
          is_paid: hPaid,
        });
      } else {
        await createHoliday({ name: hName, date: hDate, is_paid: hPaid });
      }
      await load();
      setHolidayOpen(false);
      setEditingHoliday(null);
      setHName("");
      setHDate("");
      setHPaid(true);
      addToast("success", "Holiday saved");
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Failed to save holiday");
    }
  };

  const removeHoliday = async (id: string) => {
    try {
      await deleteHoliday(id);
      await load();
      addToast("success", "Holiday deleted");
    } catch {
      addToast("error", "Failed to delete");
    }
  };

  const savePolicy = async () => {
    try {
      const cleanTiers = form.tenure_tiers
        .filter((t) => Number.isFinite(t.months) && Number.isFinite(t.days_per_month))
        .map((t) => ({
          months: Number(t.months),
          days_per_month: Number(t.days_per_month),
        }));
      await upsertPolicy({
        leave_type: form.leave_type,
        annual_allocation_days: Number(form.annual_allocation_days),
        accrual_method: form.accrual_method,
        monthly_accrual_days: Number(form.monthly_accrual_days),
        max_carryover_days: Number(form.max_carryover_days),
        requires_approval: form.requires_approval,
        min_notice_days: Number(form.min_notice_days),
        max_consecutive_days: Number(form.max_consecutive_days),
        is_active: form.is_active,
        allow_negative_balance: form.allow_negative_balance,
        accrual_cap_days: Number(form.accrual_cap_days),
        proration_method: form.proration_method,
        accrue_day_of_month: Number(form.accrue_day_of_month),
        tenure_tiers_json: cleanTiers.length > 0 ? cleanTiers : null,
      });
      await load();
      setPolicyOpen(false);
      setEditingPolicy(null);
      setForm(EMPTY_POLICY);
      addToast("success", "Policy saved");
    } catch {
      addToast("error", "Failed to save policy");
    }
  };

  const openEditPolicy = (p: LeavePolicy) => {
    setEditingPolicy(p);
    setForm({
      leave_type: p.leave_type,
      annual_allocation_days: p.annual_allocation_days,
      accrual_method: p.accrual_method,
      monthly_accrual_days: p.monthly_accrual_days,
      max_carryover_days: p.max_carryover_days,
      requires_approval: p.requires_approval,
      min_notice_days: p.min_notice_days,
      max_consecutive_days: p.max_consecutive_days,
      is_active: p.is_active,
      allow_negative_balance: !!p.allow_negative_balance,
      accrual_cap_days: p.accrual_cap_days ?? 0,
      proration_method: p.proration_method ?? "none",
      accrue_day_of_month: p.accrue_day_of_month ?? 1,
      tenure_tiers: Array.isArray(p.tenure_tiers_json)
        ? p.tenure_tiers_json
        : [],
    });
    setPolicyOpen(true);
  };

  const addTier = () =>
    setForm((f) => ({
      ...f,
      tenure_tiers: [...f.tenure_tiers, { months: 0, days_per_month: 0 }],
    }));
  const updateTier = (
    idx: number,
    field: keyof TenureTier,
    value: number
  ) =>
    setForm((f) => ({
      ...f,
      tenure_tiers: f.tenure_tiers.map((t, i) =>
        i === idx ? { ...t, [field]: value } : t
      ),
    }));
  const removeTier = (idx: number) =>
    setForm((f) => ({
      ...f,
      tenure_tiers: f.tenure_tiers.filter((_, i) => i !== idx),
    }));

  return (
    <div className="space-y-6">
      {/* Custom leave types — sits at the top because adding a new
          category is what most admins come to this page to do. */}
      <CustomLeaveTypesPanel />

      {/* Policies */}
      <Card>
        <CardHeader>
          <CardTitle>Leave policies</CardTitle>
          <Button
            size="sm"
            onClick={() => {
              setEditingPolicy(null);
              setForm(EMPTY_POLICY);
              setPolicyOpen(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Add policy
          </Button>
        </CardHeader>
        {policies.length === 0 ? (
          <p className="text-sm text-text-muted text-center py-6">
            No leave policies configured — system defaults apply.
          </p>
        ) : (
          <div className="divide-y divide-border/50">
            {policies.map((p) => (
              <div
                key={p.id}
                className="py-3 flex items-center justify-between gap-3"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-text-primary">{p.leave_type}</p>
                    <Badge variant={p.is_active ? "success" : "default"}>
                      {p.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  <p className="text-xs text-text-muted mt-0.5">
                    {p.annual_allocation_days}d/yr · {p.accrual_method.replace("_", " ")}
                    {p.max_carryover_days > 0 &&
                      ` · ${p.max_carryover_days}d carryover`}
                    {p.min_notice_days > 0 && ` · ${p.min_notice_days}d notice`}
                    {!p.requires_approval && " · auto-approve"}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => openEditPolicy(p)}>
                  <Pencil className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Holidays */}
      <Card>
        <CardHeader>
          <CardTitle>Holiday calendar</CardTitle>
          <div className="flex items-center gap-2">
            <Select
              value={String(year)}
              onChange={(e) => setYear(Number(e.target.value))}
              options={[year - 1, year, year + 1, year + 2].map((y) => ({
                value: String(y),
                label: String(y),
              }))}
            />
            <Button
              size="sm"
              onClick={() => {
                setEditingHoliday(null);
                setHName("");
                setHDate(`${year}-01-01`);
                setHPaid(true);
                setHolidayOpen(true);
              }}
            >
              <Plus className="h-4 w-4" />
              Add
            </Button>
          </div>
        </CardHeader>
        {holidays.length === 0 ? (
          <p className="text-sm text-text-muted text-center py-6">
            No holidays for {year}
          </p>
        ) : (
          <div className="divide-y divide-border/50">
            {holidays.map((h) => (
              <div
                key={h.id}
                className="py-3 flex items-center justify-between gap-3"
              >
                <div className="flex items-center gap-3 flex-1">
                  <Calendar className="h-4 w-4 text-text-muted" />
                  <div>
                    <p className="text-sm font-medium text-text-primary">
                      {h.name}
                    </p>
                    <p className="text-xs text-text-muted">
                      {formatDate(h.date)}
                      {!h.is_paid && " · unpaid"}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditingHoliday(h);
                    setHName(h.name);
                    setHDate(h.date);
                    setHPaid(h.is_paid);
                    setHolidayOpen(true);
                  }}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeHoliday(h.id)}
                >
                  <Trash2 className="h-4 w-4 text-danger" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal
        isOpen={holidayOpen}
        onClose={() => setHolidayOpen(false)}
        title={editingHoliday ? "Edit holiday" : "Add holiday"}
      >
        <div className="space-y-3">
          <Input
            label="Name"
            value={hName}
            onChange={(e) => setHName(e.target.value)}
          />
          <Input
            label="Date"
            type="date"
            value={hDate}
            onChange={(e) => setHDate(e.target.value)}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={hPaid}
              onChange={(e) => setHPaid(e.target.checked)}
              className="rounded border-border text-accent"
            />
            Paid holiday
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setHolidayOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveHoliday}>
              <Save className="h-4 w-4" />
              Save
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={policyOpen}
        onClose={() => setPolicyOpen(false)}
        title={editingPolicy ? "Edit policy" : "Add policy"}
      >
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-text-muted mb-1">
              Leave type
            </label>
            <Select
              value={form.leave_type}
              onChange={(e) => setForm({ ...form, leave_type: e.target.value })}
              options={LEAVE_TYPES.map((v) => ({ value: v, label: v }))}
              disabled={!!editingPolicy}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Annual allocation (days)"
              type="number"
              value={form.annual_allocation_days}
              onChange={(e) =>
                setForm({
                  ...form,
                  annual_allocation_days: Number(e.target.value),
                })
              }
            />
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">
                Accrual method
              </label>
              <Select
                value={form.accrual_method}
                onChange={(e) =>
                  setForm({ ...form, accrual_method: e.target.value })
                }
                options={ACCRUAL_METHODS}
              />
            </div>
            <Input
              label="Monthly accrual (days)"
              type="number"
              step="0.25"
              value={form.monthly_accrual_days}
              onChange={(e) =>
                setForm({ ...form, monthly_accrual_days: Number(e.target.value) })
              }
            />
            <Input
              label="Max carryover (days)"
              type="number"
              value={form.max_carryover_days}
              onChange={(e) =>
                setForm({ ...form, max_carryover_days: Number(e.target.value) })
              }
            />
            <Input
              label="Min notice (days)"
              type="number"
              value={form.min_notice_days}
              onChange={(e) =>
                setForm({ ...form, min_notice_days: Number(e.target.value) })
              }
            />
            <Input
              label="Max consecutive days (0=none)"
              type="number"
              value={form.max_consecutive_days}
              onChange={(e) =>
                setForm({ ...form, max_consecutive_days: Number(e.target.value) })
              }
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.requires_approval}
              onChange={(e) =>
                setForm({ ...form, requires_approval: e.target.checked })
              }
              className="rounded border-border text-accent"
            />
            Requires manager approval
          </label>
          {/* Balance gate toggle. Default OFF (= block over-drafts).
              Industry-standard for most US/EU orgs; flip on for
              special cases (e.g. medical leave with payroll
              advances). */}
          <div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.allow_negative_balance}
                onChange={(e) =>
                  setForm({
                    ...form,
                    allow_negative_balance: e.target.checked,
                  })
                }
                className="rounded border-border text-accent"
              />
              Allow employees to over-draft (request more than they have)
            </label>
            <p className="text-xs text-text-muted ml-6 mt-0.5">
              Off (recommended): requests beyond the remaining balance
              are rejected at submit time.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="rounded border-border text-accent"
            />
            Active
          </label>

          {/* Accrual engine knobs — only relevant when accrual_method=monthly */}
          {form.accrual_method === "monthly" && (
            <div className="mt-2 rounded-lg border border-border/60 p-3 space-y-3">
              <p className="text-xs uppercase tracking-wider text-text-muted">
                Monthly accrual engine
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Accrual cap (days, 0 = none)"
                  type="number"
                  step="0.25"
                  value={form.accrual_cap_days}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      accrual_cap_days: Number(e.target.value),
                    })
                  }
                />
                <div>
                  <label className="block text-xs font-medium text-text-muted mb-1">
                    Proration
                  </label>
                  <Select
                    value={form.proration_method}
                    onChange={(e) =>
                      setForm({ ...form, proration_method: e.target.value })
                    }
                    options={PRORATION_METHODS}
                  />
                </div>
                <Input
                  label="Accrue on day of month"
                  type="number"
                  min={1}
                  max={28}
                  value={form.accrue_day_of_month}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      accrue_day_of_month: Number(e.target.value),
                    })
                  }
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-text-muted">
                    Tenure tiers (override monthly rate)
                  </label>
                  <Button size="sm" variant="ghost" onClick={addTier}>
                    <Plus className="h-3.5 w-3.5" />
                    Add tier
                  </Button>
                </div>
                {form.tenure_tiers.length === 0 ? (
                  <p className="text-xs text-text-muted">
                    No tiers — base monthly rate applies to everyone.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {form.tenure_tiers.map((t, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Input
                          placeholder="Months"
                          type="number"
                          value={t.months}
                          onChange={(e) =>
                            updateTier(i, "months", Number(e.target.value))
                          }
                        />
                        <span className="text-xs text-text-muted">→</span>
                        <Input
                          placeholder="Days/month"
                          type="number"
                          step="0.25"
                          value={t.days_per_month}
                          onChange={(e) =>
                            updateTier(
                              i,
                              "days_per_month",
                              Number(e.target.value)
                            )
                          }
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeTier(i)}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-danger" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setPolicyOpen(false)}>
              Cancel
            </Button>
            <Button onClick={savePolicy}>Save</Button>
          </div>
        </div>
      </Modal>

      {/* Accrual runner */}
      <AccrualRunnerPanel />
    </div>
  );
}
